import {
  DesktopContainer,
  LoadingSpinner,
  WarrantyInfo,
} from '@rabbit/elements/shared-components';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useConsumerHoldingEditor,
  useGetConsumerHoldings,
} from '@rabbit/bizproc/react';
import { useMyConsumerPersona } from '@rabbit/data/portal';
import { getWarrantyInfo } from '@rabbit/olive/utils/helpers';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '@rabbit/app-context';
import { BL_Warranty } from '@rabbit/bizproc/core';
import { FBD_Warranty } from '@rabbit/data/types';

/* eslint-disable-next-line */
export interface HoldingSingleWarrantyInfoViewProps {}

export interface ItemProps {
  id?: string;
  title: string;
  description?: string;
  included?: string;
}

export interface InfoInterface {
  title: string;
  type: string;
  items?: ItemProps[];
  children?: any;
}

export function HoldingSingleWarrantyInfoView(
  props: HoldingSingleWarrantyInfoViewProps
) {
  const { config } = useContext(AppContext);
  const location = useLocation();
  const holdingId = location.pathname.split('/')[2];
  const persona = useMyConsumerPersona();
  const { t } = useTranslation();
  const { body } = useConsumerHoldingEditor(
    persona?.personaId ?? '',
    holdingId
  );
  const { getCurrentHoldingProxy } = useGetConsumerHoldings(
    persona.personaId ?? ''
  );

  if (!body) return <LoadingSpinner size="md" extraClasses="mt-5" />;

  const navigate = useNavigate();
  const { tenantInfo } = useContext(AppContext);
  const [warrantyInfo, setWarrantyInfo] = useState<InfoInterface[]>([]);
  const [warrantyTerms, setWarrantyTerms] = useState<InfoInterface[]>([]);

  useEffect(() => {
    const getWarranty = async () => {
      try {
        const warranty = await getWarrantyInfo(t('tenantLink'));

        // Check if warranty terms should be retrieved from Firestore
        if (config.WARRANTIES.WARRANTY_TC_FROM_FIRESTORE) {
          const currentHoldingProxy = await getCurrentHoldingProxy(holdingId);

          // Add type guard to check if currentHoldingProxy is not an Error
          if (currentHoldingProxy && !(currentHoldingProxy instanceof Error)) {
            const templateLink = await BL_Warranty.getWarrantyFromHoldingId(
              holdingId
            );
            if (templateLink) {
              // Retrieve warranty terms and info from Firestore
              const latestWarrantyTermsInfo =
                await BL_Warranty.getWarrantyTermsAndInfo(
                  templateLink?.templateLink!
                );
              if (latestWarrantyTermsInfo) {
                setWarrantyInfo(
                  latestWarrantyTermsInfo.info as InfoInterface[]
                );
                setWarrantyTerms(
                  latestWarrantyTermsInfo.terms as InfoInterface[]
                );
                return; // Exit early once warranty info is set
              }
            }
          }
        }

        // Fallback to default warranty info if Firestore data isn't available
        if (warranty) {
          setWarrantyInfo(warranty.warranty_info);
          setWarrantyTerms(warranty.warranty_terms);
        }
      } catch (error) {
        console.error('Error retrieving warranty information:', error);
      }
    };

    getWarranty();
  }, [
    holdingId,
    persona.personaId,
    config.WARRANTIES.WARRANTY_TC_FROM_FIRESTORE,
    t,
  ]);

  return (
    <div className="flex flex-wrap items-start justify-center">
      <div className="w-full md:basis-1/2">
        <DesktopContainer
          title={
            tenantInfo?.name === 'NuCover'
              ? 'Care Plan Summary'
              : 'Warranty Information'
          }
          goBack={() => navigate(-1)}
        >
          {warrantyInfo
            .filter(
              (data: InfoInterface) => data.title !== 'Consumer Guarantee'
            )
            .map((data: InfoInterface, index: number) => (
              <WarrantyInfo key={index} data={data} />
            ))}
        </DesktopContainer>
      </div>
      {warrantyTerms?.length !== 0 && (
        <div className="w-full md:basis-1/2">
          <DesktopContainer title={'Warranty terms'}>
            {warrantyTerms.map((data: InfoInterface, index: number) => (
              <WarrantyInfo key={index} data={data} />
            ))}
          </DesktopContainer>
        </div>
      )}
      <div className="pt-8 lg:px-8">
        {warrantyInfo
          .filter((data: InfoInterface) => data.title === 'Consumer Guarantee')
          .map((data: InfoInterface, index: number) => (
            <WarrantyInfo key={index} data={data} />
          ))}
      </div>
    </div>
  );
}

export default HoldingSingleWarrantyInfoView;
